import React, { useState } from "react";
import { Alert, Button, Modal, Tabs, Tag } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { round } from "lodash";
import pluralize from "pluralize";

import {
  describeReturnTier,
  describeReturnTiers,
  formatMoney,
  metersToMiles,
  resolveReturnDistanceTier,
  RETURN_TIER
} from "helper/returnPricing";
import "./RoutePricingInfo.scss";

/**
 * Backs out the quantity (miles/hours) that produced a stored dollar amount, using the
 * configured rate, so the breakdown matches what was actually stored on the estimate.
 */
const backOutQuantity = (amount, rate, fallback) => {
  if (rate) return round((amount || 0) / rate, 2);
  return fallback != null ? round(fallback, 2) : 0;
};

/**
 * /routes/:id returns `{ client, driver }` (latest of each); /admin/routes/:id returns the
 * raw array. Accept both.
 */
const latestEstimates = (routeEstimates) => {
  if (!routeEstimates) return { client: null, driver: null };
  if (!Array.isArray(routeEstimates)) return routeEstimates;
  const latest = (type) =>
    [...routeEstimates]
      .filter((estimate) => estimate.type === type)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] || null;
  return { client: latest("client_estimate"), driver: latest("driver_estimate") };
};

const SummaryRow = ({ label, value, suffix = "" }) => (
  <div className="pricing-info-row">
    <span className="pricing-info-label">{label}</span>
    <span className="pricing-info-value">
      {value}
      {suffix}
    </span>
  </div>
);

const FormulaRow = ({ label, quantity, unit, rate, total, muted }) => (
  <div
    className={`pricing-info-row pricing-info-row--formula${muted ? " is-muted" : ""}`}
  >
    <span className="pricing-info-label">{label}</span>
    <span className="pricing-info-formula">
      {quantity} {unit} &times; {formatMoney(rate)}
    </span>
    <span className="pricing-info-value">{formatMoney(total)}</span>
  </div>
);

/**
 * Return to pickup / return failed packages are compliance-only flags - they never gate the
 * return-distance charge, so always render the discrete value actually stored on the route
 * rather than an ambiguous "client default" placeholder.
 */
const FlagTag = ({ value }) => (
  <Tag color={value ? "green" : "default"}>{value ? "On" : "Off"}</Tag>
);

/**
 * By contract the return-distance rate is always billed, subject to the Free / Short / Long
 * tiers - so "billed" here means the resolved tier isn't the free one, not whether either
 * return-process toggle is on.
 */
const BilledTag = ({ billed }) => (
  <Tag color={billed ? "green" : "red"}>{billed ? "Y" : "No (under free threshold)"}</Tag>
);

/**
 * Return-distance tier table for one rate card, with the band this route falls in marked.
 * The bands are defined once, in resolveReturnDistanceTier (backend) / helper/returnPricing
 * (frontend); this only renders them.
 */
const ReturnTierTable = ({ rate, miles }) => {
  const tiers = describeReturnTiers(rate);
  const resolved = resolveReturnDistanceTier(miles, rate);
  const rows = [tiers.free, tiers.short, tiers.long].filter(Boolean);
  return (
    <div className="return-tier-table">
      {rows.map((row) => {
        const applied = miles > 0 && row.tier === resolved.tier;
        return (
          <div
            key={row.label}
            className={`return-tier-row${applied ? " is-applied" : ""}${
              row.tier === RETURN_TIER.NONE ? " is-free" : ""
            }`}
          >
            <span className="return-tier-label">{row.label}</span>
            <span className="return-tier-rate">
              {row.tier === RETURN_TIER.NONE
                ? "not billed"
                : `${formatMoney(row.rate)}/mi × whole trip`}
            </span>
            <span className="return-tier-flag">
              {applied ? "applies to this route" : ""}
            </span>
          </div>
        );
      })}
      <div className="return-tier-row is-time">
        <span className="return-tier-label">Return time (any tier)</span>
        <span className="return-tier-rate">
          {formatMoney(rate?.pricePerReturnTime)}/hr
        </span>
        <span className="return-tier-flag" />
      </div>
    </div>
  );
};

const RateBreakdown = ({ estimate, rate, route }) => {
  if (!estimate || !rate) {
    return (
      <div className="pricing-info-row">
        <span className="pricing-info-label">
          No pricing data available for this route.
        </span>
      </div>
    );
  }

  const distanceMiles = backOutQuantity(
    estimate.priceDistanceOnRoute,
    rate.pricePerDistanceOnRoute,
    metersToMiles(route?.distance)
  );
  // What the estimate was actually priced on. Older estimates do not echo the inputs back,
  // so fall back to the route's stored return leg (0 when return-to-pickup was off).
  const returnMiles =
    estimate.totalReturnDistance != null
      ? Number(estimate.totalReturnDistance)
      : round(metersToMiles(route?.returnDistance), 2);
  const returnHours =
    estimate.totalReturnTime != null
      ? Number(estimate.totalReturnTime)
      : round((route?.returnDuration || 0) / 3600, 2);
  const resolved = resolveReturnDistanceTier(returnMiles, rate);
  const chargedReturn =
    (estimate.priceReturnDistanceShort || 0) + (estimate.priceReturnDistanceLong || 0);
  const expectedReturn = round(returnMiles * resolved.rate, 2);
  const additionalHours = backOutQuantity(
    estimate.priceAdditionalTimeOnRoute,
    rate.pricePerAdditionalTimeOnRoute,
    null
  );

  const warnings = [];
  if (returnMiles > 0 && resolved.tier === RETURN_TIER.NONE) {
    warnings.push(
      `This route's ${returnMiles} mi return is at or below the free threshold (${resolved.maxReturnDistanceShort} mi), so no per-mile return charge applies.`
    );
  }
  if (returnMiles > 0 && resolved.tier !== RETURN_TIER.NONE && !resolved.rate) {
    warnings.push(
      `The ${describeReturnTier(
        resolved.tier,
        rate
      )} tier applies but its rate is ${formatMoney(0)}/mi in Settings.`
    );
  }
  if (Math.abs(chargedReturn - expectedReturn) >= 0.01) {
    warnings.push(
      `Stored estimate charged ${formatMoney(
        chargedReturn
      )} for return distance; the current rate card would charge ${formatMoney(
        expectedReturn
      )}. The estimate was priced with the rules or rates in force when the route was created.`
    );
  }

  return (
    <div className="pricing-breakdown">
      <FormulaRow
        label="Flat Rate"
        quantity={1}
        unit="flat"
        rate={estimate.basePrice}
        total={estimate.basePrice}
      />
      <FormulaRow
        label="Distance on Route"
        quantity={distanceMiles}
        unit="mi"
        rate={rate.pricePerDistanceOnRoute}
        total={estimate.priceDistanceOnRoute}
      />

      <div className="pricing-subsection">
        <div className="pricing-subsection-title">
          Return distance{" "}
          <span className="pricing-subsection-sub">
            {returnMiles > 0
              ? `${returnMiles} mi → ${describeReturnTier(resolved.tier, rate)}`
              : "none on this route"}
          </span>
        </div>
        <ReturnTierTable rate={rate} miles={returnMiles} />
      </div>

      <FormulaRow
        label="Return Distance – Short"
        quantity={backOutQuantity(
          estimate.priceReturnDistanceShort,
          rate.pricePerReturnDistanceShort,
          resolved.tier === RETURN_TIER.SHORT ? returnMiles : 0
        )}
        unit="mi"
        rate={rate.pricePerReturnDistanceShort}
        total={estimate.priceReturnDistanceShort}
        muted={!estimate.priceReturnDistanceShort}
      />
      <FormulaRow
        label="Return Distance – Long"
        quantity={backOutQuantity(
          estimate.priceReturnDistanceLong,
          rate.pricePerReturnDistanceLong,
          resolved.tier === RETURN_TIER.LONG ? returnMiles : 0
        )}
        unit="mi"
        rate={rate.pricePerReturnDistanceLong}
        total={estimate.priceReturnDistanceLong}
        muted={!estimate.priceReturnDistanceLong}
      />
      <FormulaRow
        label="Return Time"
        quantity={backOutQuantity(
          estimate.priceReturnTime,
          rate.pricePerReturnTime,
          returnHours
        )}
        unit="hrs"
        rate={rate.pricePerReturnTime}
        total={estimate.priceReturnTime}
        muted={!estimate.priceReturnTime}
      />
      {warnings.map((warning) => (
        <Alert key={warning} type="warning" showIcon message={warning} className="mb-2" />
      ))}
      <FormulaRow
        label="Additional Time on Route (overtime)"
        quantity={additionalHours}
        unit="hrs"
        rate={rate.pricePerAdditionalTimeOnRoute}
        total={estimate.priceAdditionalTimeOnRoute}
      />
      <SummaryRow label="Subtotal" value={formatMoney(estimate.subTotal)} />
      <SummaryRow label="Estimate Price" value={formatMoney(estimate.estimatePrice)} />
    </div>
  );
};

const RoutePricingInfo = ({ route }) => {
  const [visible, setVisible] = useState(false);

  const travelMins = route?.travelMins || 0;
  const serviceMins = route?.serviceMins || 0;
  const waitingMins = route?.waitingMins || 0;
  const workingMins = route?.workingMins || 0;
  const workingPercentage = route?.workingPercentage || 0;
  const distanceKm = route?.distance ? round(route.distance / 1000, 2) : 0;
  const returnKm = route?.returnDistance ? round(route.returnDistance / 1000, 2) : 0;
  const returnMiles = round(metersToMiles(route?.returnDistance), 2);
  const returnMins = route?.returnDuration ? Math.round(route.returnDuration / 60) : 0;

  const { client: clientEstimate, driver: driverEstimate } = latestEstimates(
    route?.routeEstimates
  );
  const clientRate = route?.vehicleRates?.client;
  const driverRate = route?.vehicleRates?.driver;
  const hasPricingBreakdown = Boolean(
    (clientEstimate && clientRate) || (driverEstimate && driverRate)
  );
  const billingRate = clientRate || driverRate;
  const isReturnBilled = billingRate
    ? resolveReturnDistanceTier(returnMiles, billingRate).tier !== RETURN_TIER.NONE
    : returnMiles > 0;

  return (
    <div className="route-pricing-info-container">
      <Button
        type="default"
        icon={<InfoCircleOutlined />}
        className="route-pricing-info-button"
        block
        onClick={() => setVisible(true)}
      >
        Route Pricing Info
      </Button>

      <Modal
        title="Route Pricing Info"
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={760}
        className="route-pricing-modal"
      >
        <div className="route-pricing-popover">
          <h4 className="pricing-section-title">Route Metrics</h4>
          <div className="pricing-metrics-grid">
            <SummaryRow
              label="Travel Time"
              value={travelMins}
              suffix={` ${pluralize("min", travelMins)}`}
            />
            <SummaryRow
              label="Service Time"
              value={serviceMins}
              suffix={` ${pluralize("min", serviceMins)}`}
            />
            <SummaryRow
              label="Waiting Time"
              value={waitingMins}
              suffix={` ${pluralize("min", waitingMins)}`}
            />
            <SummaryRow
              label="Working Time"
              value={workingMins}
              suffix={` ${pluralize("min", workingMins)}`}
            />
            <SummaryRow label="Working Percentage" value={workingPercentage} suffix="%" />
            <SummaryRow label="Distance" value={distanceKm} suffix=" km" />
          </div>

          <h4 className="pricing-section-title pricing-section-title--breakdown">
            Return Process
          </h4>
          <div className="pricing-metrics-grid">
            <SummaryRow
              label="Return to pickup after all stops"
              value={<FlagTag value={route?.returnToPickupEnabled} />}
            />
            <SummaryRow
              label="Return failed packages"
              value={<FlagTag value={route?.returnOnFailedEnabled} />}
            />
            <SummaryRow
              label="Calculated Return Distance"
              value={`${returnMiles} mi`}
              suffix={` (${returnKm} km)`}
            />
            <SummaryRow label="Billed?" value={<BilledTag billed={isReturnBilled} />} />
            <SummaryRow
              label="Return Time"
              value={returnMins}
              suffix={` ${pluralize("min", returnMins)}`}
            />
          </div>

          {hasPricingBreakdown && (
            <>
              <h4 className="pricing-section-title pricing-section-title--breakdown">
                Pricing Breakdown
              </h4>
              <Tabs size="large" defaultActiveKey="client">
                <Tabs.TabPane tab="Client Rate" key="client">
                  <RateBreakdown
                    estimate={clientEstimate}
                    rate={clientRate}
                    route={route}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Driver Rate" key="driver">
                  <RateBreakdown
                    estimate={driverEstimate}
                    rate={driverRate}
                    route={route}
                  />
                </Tabs.TabPane>
              </Tabs>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default RoutePricingInfo;
